import { getCustomLengthArray } from '@/utils/arrays';
import { DS_COLOR } from '@calm/evexia-design-tokens';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    LinearProgress,
    SxProps,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { GoalSolid } from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { useIntl } from 'react-intl';

interface SessionProgressBarProps {
    totalSteps: number;
    currentStep?: number;
    isClincialProgram?: boolean;
    sx?: SxProps<Theme>;
}

const styleSessionProgressBar = {
    height: '8px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '1px',
    position: 'relative',
};

const styleSessionProgressItem = {
    height: '8px',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_COLOR.LIGHT_ACTION_DISABLED
            : DS_COLOR.WHITE_40P,

    '&:first-of-type': {
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px',
    },
};
const styleLastProgressItem = {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
};
const styleActiveItem = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_COLOR.LIGHT_INFO_MAIN
            : DS_COLOR.DARK_INFO_MAIN,
};
const styleProgressMarker = {
    position: 'absolute',
    color: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_COLOR.LIGHT_INFO_DARK
            : DS_COLOR.DARK_INFO_MAIN,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_COLOR.LIGHT_BACKGROUND_DEFAULT
            : DS_COLOR.DARK_INFO_DARK,
    height: '28px',
    width: '28px',
    borderRadius: '28px',
    boxSizing: 'border-box',
    border: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? `1px solid ${DS_COLOR.BLACK_12P}`
            : `1px solid ${DS_COLOR.WHITE_40P}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};
const styleProgressIcon = {
    width: '20px',
    height: '20px',
};
const styleProgressBar = {
    mt: 2,
};
const styleProgressContainer = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    gap: '16px',
    my: 7,
};
const halfOfMarker = 14;

const SessionProgressBar = ({
    currentStep = 0,
    totalSteps,
    isClincialProgram = false,
    sx,
}: SessionProgressBarProps) => {
    const steps = getCustomLengthArray(totalSteps);
    const { formatMessage } = useIntl();
    const completionPercentage = Math.round(currentStep * (100 / totalSteps));

    if (!isClincialProgram) {
        return (
            <Box sx={sx}>
                <Typography variant="body1" color="text.body">
                    {formatMessage(commonMessages['common.session_of'], {
                        numerator: currentStep,
                        denominator: totalSteps,
                    })}
                </Typography>
                <LinearProgress
                    data-testid="program-progress"
                    variant="determinate"
                    color="primary"
                    aria-label={formatMessage(
                        commonMessages['common.percent_complete'],
                        {
                            percentage: completionPercentage,
                        }
                    )}
                    value={completionPercentage}
                    sx={styleProgressBar}
                    withCurvedCorners
                />
            </Box>
        );
    }

    return (
        <Box sx={sxAppend(styleProgressContainer, sx)}>
            <Box
                data-testid="program-progress"
                sx={styleSessionProgressBar}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={totalSteps}
                aria-valuenow={currentStep}
                aria-label={formatMessage(
                    commonMessages['common.percent_complete'],
                    {
                        percentage: completionPercentage,
                    }
                )}
            >
                {steps.map((_, index) => (
                    <Box
                        key={index}
                        sx={sxAppend(
                            styleSessionProgressItem,
                            {
                                width: `${100 / totalSteps}%`,
                            },
                            index < currentStep && styleActiveItem,
                            index === steps.length - 1 && styleLastProgressItem
                        )}
                    />
                ))}
                <Box
                    sx={sxAppend(styleProgressMarker, {
                        left: `calc(${
                            currentStep * (100 / totalSteps)
                        }% - ${halfOfMarker}px)`,
                    })}
                >
                    <GoalSolid sx={styleProgressIcon} />
                </Box>
            </Box>
            <Typography
                variant="body2"
                component="p"
                color="text.sub"
                textAlign="center"
            >
                {formatMessage(commonMessages['common.session_of'], {
                    numerator: currentStep,
                    denominator: totalSteps,
                })}
            </Typography>
        </Box>
    );
};

export default SessionProgressBar;
